import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ToastType, showToast } from "@is/components/Toasts";
import { PasscodeOptions, TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import {
  useCollection,
  useCollectionsList,
  useConfiguration,
  useStoreDevice,
} from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";

type Props = {
  styleId: string;
  variantId: string;
  onClose: () => void;
};

const useManageAddToCollectionModal = ({
  styleId,
  variantId,
  onClose,
}: Props) => {
  const { t } = useTranslation(TranslationContext.General);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [isPending, setIsPending] = useState(false);

  const { updateViewState, hasProvidedPasscode } =
    useContext(ViewStateContext).content;

  const { staffDashboard } = useConfiguration();

  const { addStyleToCollection } = useCollection(selectedCollectionId);

  const { collections, isLoading, revalidateCollections } = useCollectionsList({
    filterOutOfStockItems: false,
  });

  const { data: storeInfo } = useStoreDevice();

  const allCollections = useMemo(() => {
    if (!collections) return [];

    // Brand-wide collections are managed from the admin dashboard and cannot receive items
    // from a store — the backend refuses it, so they are not offered as targets here.
    return collections?.items?.filter((item) => item.id && !item.isBrandWide);
  }, [collections]);

  const passcode = useMemo(() => {
    if (!staffDashboard || !storeInfo) return [];

    if (staffDashboard.passcodeOption === PasscodeOptions.CUSTOM) {
      return staffDashboard.customPasscode.split("");
    }

    if (staffDashboard.passcodeOption === PasscodeOptions.STOREID) {
      return storeInfo.storeId.split("");
    }

    return [];
  }, [staffDashboard, storeInfo]);

  const buttonState: "disabled" | "pending" | undefined = useMemo(() => {
    if (!selectedCollectionId) return "disabled";
    if (isPending) return "pending";

    return undefined;
  }, [selectedCollectionId, isPending]);

  const handleCollectionClick = useCallback(
    (collectionId: string) => setSelectedCollectionId(collectionId),
    []
  );

  const handlePasscodeViewClose = useCallback(() => {
    setCurrentStep(2);
    updateViewState({ hasProvidedPasscode: true });
  }, [updateViewState]);

  const handleAddToCollection = useCallback(async () => {
    setIsPending(true);

    const result = await addStyleToCollection(styleId, variantId);

    if (result) {
      showToast({
        message: t("Successfully added to collection"),
        type: ToastType.Success,
      });

      trackEvent({
        eventType: TrackingEventTypes.AddedToCollectionFromPDP,
        data: {
          collectionId: selectedCollectionId,
          styleId: styleId,
          variantId: variantId,
        },
      });

      revalidateCollections();

      setIsPending(false);
      onClose();
    } else {
      showToast({
        message: t("Could not be added to the collection. Please try again."),
        type: ToastType.Error,
      });
    }

    setIsPending(false);
  }, [
    styleId,
    variantId,
    selectedCollectionId,
    addStyleToCollection,
    revalidateCollections,
    onClose,
    t,
  ]);

  useEffect(() => {
    if (!staffDashboard || isLoading) return;

    const needsPasscode =
      [PasscodeOptions.STOREID, PasscodeOptions.CUSTOM].includes(
        staffDashboard.passcodeOption
      ) && !hasProvidedPasscode;

    if (needsPasscode) {
      setCurrentStep(1);

      trackEvent({
        eventType: TrackingEventTypes.StaffPasscodePageOpened,
        data: {},
      });

      return;
    }

    setCurrentStep(2);
  }, [hasProvidedPasscode, staffDashboard, isLoading]);

  return {
    t,
    currentStep,
    passcode,
    allCollections,
    selectedCollectionId,
    buttonState,
    handleCollectionClick,
    handlePasscodeViewClose,
    handleAddToCollection,
  };
};

export default useManageAddToCollectionModal;
